import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import { AUTH_CHANGED_EVENT, loadAuthSession } from "../../../services/authStorage";
import { getShopReservationsForCurrentUser, ShopReservationDto, ShopReservationStatus } from "../../../services/shopApi";
import { formatGermanDateTime } from "../../../utils/dateTime";
import {
  ContentContainer,
  Eyebrow,
  HeroBanner,
  HeroText,
  HeroTitle,
  ItemCard,
  ItemList,
  ItemMeta,
  ItemTitle,
  ItemTitleRow,
  NoticeCard,
  NoticeText,
  NoticeTitle,
  ReservationCard,
  ReservationHeader,
  ReservationSubline,
  ReservationTitle,
  StatusBadge,
} from "../ShopReservationsManagement/styles";

const STATUS_LABELS: Record<ShopReservationStatus, string> = {
  offen: "Offen",
  bestaetigt: "Bestätigt",
  erledigt: "Erledigt",
  storniert: "Storniert",
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const MyShopReservations = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [reservations, setReservations] = useState<ShopReservationDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const sync = () => setSession(loadAuthSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  const token = session?.token ?? "";

  useEffect(() => {
    if (!session) {
      navigate("/konto", { replace: true });
      return;
    }

    const loadReservations = async () => {
      try {
        const result = await getShopReservationsForCurrentUser(token);
        setReservations(result);
        setLoadError("");
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : "Reservierungen konnten nicht geladen werden.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadReservations();
  }, [navigate, session, token]);

  const totalOrders = reservations.length;
  const totalItems = useMemo(
    () => reservations.reduce((sum, reservation) => sum + reservation.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0),
    [reservations]
  );

  return (
    <SinglePage
      title="Meine Reservierungen"
      heroBanner={
        <HeroBanner>
          <Eyebrow>Mitgliederbereich</Eyebrow>
          <HeroTitle>Deine Shop-Reservierungen.</HeroTitle>
          <HeroText>
            Hier findest du alle eigenen Reservierungen aus dem Vereinsshop inklusive Status, Artikeln und internen
            Rueckmeldungen aus der Shopverwaltung.
          </HeroText>
        </HeroBanner>
      }
      content={
        <ContentContainer>
          <NoticeCard>
            <NoticeTitle>Reservierungsübersicht</NoticeTitle>
            <NoticeText>
              {isLoading
                ? "Reservierungen werden geladen."
                : loadError
                  ? loadError
                  : `${totalOrders} Reservierungen mit insgesamt ${totalItems} Artikeln gefunden.`}
            </NoticeText>
          </NoticeCard>

          {!isLoading && !loadError && reservations.length === 0 ? (
            <NoticeCard>
              <NoticeTitle>Noch keine Reservierungen</NoticeTitle>
              <NoticeText>
                Du hast aktuell noch keine Reservierung im Shop aufgegeben. Sobald du etwas bestellst, erscheint es hier.
              </NoticeText>
            </NoticeCard>
          ) : null}

          {!isLoading && !loadError
            ? reservations.map((reservation) => {
                const total = reservation.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

                return (
                  <ReservationCard key={reservation.id}>
                    <ReservationHeader>
                      <div>
                        <ReservationTitle>Reservierung {reservation.id.slice(0, 8)}</ReservationTitle>
                        <ReservationSubline>
                          Eingegangen am {formatGermanDateTime(reservation.createdAtUtc)} | Letzte Aenderung {formatGermanDateTime(reservation.updatedAtUtc)}
                        </ReservationSubline>
                        <ReservationSubline>Kleidlesnummer {reservation.user.kleidlesnummer}</ReservationSubline>
                      </div>
                      <StatusBadge>{STATUS_LABELS[reservation.status]}</StatusBadge>
                    </ReservationHeader>

                    <ItemList>
                      {reservation.items.map((item) => (
                        <ItemCard key={item.id}>
                          <ItemTitleRow>
                            <ItemTitle>
                              {item.productName} x {item.quantity}
                            </ItemTitle>
                            <ItemMeta>{formatCurrency(item.unitPrice * item.quantity)}</ItemMeta>
                          </ItemTitleRow>
                          <ItemMeta>{item.selectedOptionsSummary}</ItemMeta>
                          <ItemMeta>
                            Einzelpreis {formatCurrency(item.unitPrice)} | Artikel {item.productSlug}
                          </ItemMeta>
                        </ItemCard>
                      ))}
                    </ItemList>

                    <NoticeCard style={{ padding: "1rem 1.1rem" }}>
                      <NoticeTitle>Details</NoticeTitle>
                      <NoticeText>Gesamtsumme: {formatCurrency(total)}</NoticeText>
                      <NoticeText>
                        Shop-Notiz: {reservation.managerNote?.trim() ? reservation.managerNote : "Noch keine Rueckmeldung hinterlegt."}
                      </NoticeText>
                    </NoticeCard>
                  </ReservationCard>
                );
              })
            : null}
        </ContentContainer>
      }
    />
  );
};

export default MyShopReservations;
